import { useEffect, useId, useState } from 'react';

import { Avatar, Badge, Button, Icon, IconButton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Textarea } from '@ds/forms';
import { EnviarMensagem } from '@/comuns/EnviarMensagem';
import { dataSimples, moeda, proximoContato } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { TOM_DA_SITUACAO, etapasAtivas } from '@/data/funis';
import { dados } from '@/data/porta';
import type { CampanhaDeAnuncio, EdicaoDeLead, Funil, Lead, Profissional } from '@/data/tipos';

export interface DetalheDoLeadProps {
    lead: Lead | null;
    /** Abre já em modo de edição — é o que o lápis da linha pede. */
    editando?: boolean;
    onClose: () => void;
    onSalvo: () => void;
    onVerNoFunil: (lead: Lead) => void;
}

/** Nome comparável: a lista de leads escreve "Ana Prado"; a equipe, às vezes, "Dra. Ana Prado". */
function chave(nome: string): string {
    return nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/^dra?\.?\s+/, '').trim();
}

interface Formulario {
    nome: string;
    telefone: string;
    origem: string;
    interesse: string;
    responsavelId: string;
    valorEstimadoCentavos: number;
    proximoContatoEm: string;
    etapaId: string;
    campanhaDeAnuncioId: string;
}

/** Como o lead chegou à campanha de anúncio (0162), dito para quem lê. */
const FORMA_DA_ATRIBUICAO: Record<NonNullable<Lead['atribuicao']>, string> = {
    'whatsapp-anuncio': 'pela conversa que começou no anúncio',
    manual: 'escolhida pela equipe',
    utm: 'pelo link da campanha',
};

function formularioDe(lead: Lead, equipe: Profissional[]): Formulario {
    return {
        nome: lead.nome,
        telefone: lead.telefone,
        origem: lead.origem === '—' ? '' : lead.origem,
        interesse: lead.interesse === '—' ? '' : lead.interesse,
        responsavelId: equipe.find((p) => chave(p.nome) === chave(lead.responsavel))?.id ?? '',
        valorEstimadoCentavos: lead.valorEstimadoCentavos,
        proximoContatoEm: lead.proximoContatoEm ?? '',
        etapaId: lead.etapaId,
        campanhaDeAnuncioId: lead.campanhaDeAnuncioId ?? '',
    };
}

export function DetalheDoLead({ lead, editando = false, onClose, onSalvo, onVerNoFunil }: DetalheDoLeadProps) {
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const idNome = useId();
    const idTelefone = useId();
    const idOrigem = useId();
    const idOrigens = useId();
    const idInteresse = useId();
    const idResponsavel = useId();
    const idValor = useId();
    const idProximo = useId();
    const idEtapa = useId();
    const idNota = useId();
    const idCampanha = useId();

    const [edicao, setEdicao] = useState(editando);
    const [equipe, setEquipe] = useState<Profissional[]>([]);
    const [origens, setOrigens] = useState<string[]>([]);
    const [funis, setFunis] = useState<Funil[]>([]);
    const [campanhas, setCampanhas] = useState<CampanhaDeAnuncio[]>([]);
    const [form, setForm] = useState<Formulario | null>(null);
    const [nota, setNota] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        let vivo = true;
        /* Sem a equipe, o responsável fica sem lista; sem as origens, o campo
           só perde a sugestão; sem os pipelines, a etapa não se troca aqui.
           Nenhuma dessas faltas impede editar o resto. */
        dados().opcoesDoAgendamento()
            .then((o) => { if (vivo) setEquipe(o.profissionais); })
            .catch((e: unknown) => { if (vivo) setErro(`A equipe não carregou: ${e instanceof Error ? e.message : 'tente de novo'}.`); });
        dados().origensDeLead()
            .then((o) => { if (vivo) setOrigens(o); })
            .catch(() => { if (vivo) setOrigens([]); });
        /* Sem conta de anúncios, ou sem permissão de marketing, vem vazio e o campo some. */
        dados().campanhasDeAnuncio()
            .then((c) => { if (vivo) setCampanhas(c); })
            .catch(() => { if (vivo) setCampanhas([]); });
        dados().funis()
            .then((f) => { if (vivo) setFunis(f); })
            .catch((e: unknown) => { if (vivo) setErro(`Os pipelines não carregaram: ${e instanceof Error ? e.message : 'tente de novo'}.`); });
        return () => { vivo = false; };
    }, []);

    useEffect(() => {
        if (!lead) return;
        setEdicao(editando);
        setNota('');
        setErro(null);
    }, [lead, editando]);

    useEffect(() => {
        if (lead) setForm(formularioDe(lead, equipe));
    }, [lead, equipe]);

    if (!lead || !form) return null;

    const funil = funis.find((f) => f.id === lead.funilId);
    /* Mais de um pipeline: o nome dele acompanha a etapa, senão "Contatado" não
       diz de qual quadro se está falando. */
    const etapaComFunil = funil && funis.length > 1 ? `${funil.nome} · ${lead.etapa}` : lead.etapa;
    const original = formularioDe(lead, equipe);
    const mudouForm = JSON.stringify(form) !== JSON.stringify(original);
    const sujo = (edicao && mudouForm) || nota.trim() !== '';
    const alterar = <C extends keyof Formulario>(campo: C, valor: Formulario[C]) => {
        setErro(null);
        setForm((f) => (f ? { ...f, [campo]: valor } : f));
    };
    const texto = proximoContato(lead.proximoContatoEm);
    const atrasado = texto.startsWith('Atrasado');

    const registrarNota = async (): Promise<boolean> => {
        if (!nota.trim()) return true;
        await dados().registrarNoHistoricoDoContato(lead.contatoId, nota);
        setNota('');
        return true;
    };

    const salvar = async (): Promise<boolean> => {
        if (edicao && !form.nome.trim()) {
            setErro('O nome não pode ficar vazio.');
            return false;
        }
        setSalvando(true);
        try {
            /* Só o que mudou. Nome e telefone, que o adaptador descartava, e o
               responsável, que vinha de uma lista fixa, agora gravam. */
            const mudancas: EdicaoDeLead = {};
            if (edicao) {
                if (form.nome !== original.nome) mudancas.nome = form.nome;
                if (form.telefone !== original.telefone) mudancas.telefone = form.telefone;
                if (form.origem !== original.origem) mudancas.origem = form.origem;
                if (form.interesse !== original.interesse) mudancas.interesse = form.interesse;
                if (form.responsavelId !== original.responsavelId) mudancas.responsavelId = form.responsavelId;
                if (form.valorEstimadoCentavos !== original.valorEstimadoCentavos) mudancas.valorEstimadoCentavos = form.valorEstimadoCentavos;
                if (form.proximoContatoEm !== original.proximoContatoEm) mudancas.proximoContatoEm = form.proximoContatoEm;
                if (form.etapaId !== original.etapaId) mudancas.etapaId = form.etapaId;
                if (form.campanhaDeAnuncioId !== original.campanhaDeAnuncioId) mudancas.campanhaDeAnuncioId = form.campanhaDeAnuncioId;
            }
            const temNota = nota.trim() !== '';
            if (Object.keys(mudancas).length > 0) await dados().atualizarLead(lead.id, mudancas);
            if (temNota) await registrarNota();
            aviso({
                title: Object.keys(mudancas).length > 0 ? 'Lead atualizado' : 'Contato registrado no histórico',
                description: form.nome,
            });
            onSalvo();
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const avancar = async () => {
        setSalvando(true);
        try {
            const atualizado = await dados().avancarEtapaDoLead(lead.id);
            aviso({ title: 'Lead avançado', description: `${atualizado.nome} · ${atualizado.etapa}` });
            onSalvo();
            onClose();
        } catch (e) {
            /* A etapa final de ganho e a de perda não têm próxima: o adaptador
               recusa e a recusa aparece, em vez de o botão fingir que fez. */
            aviso({ title: 'Não dá para avançar', description: e instanceof Error ? e.message : undefined, tone: 'warning' });
        } finally {
            setSalvando(false);
        }
    };

    const marcarPerdido = async () => {
        const ok = await confirmar({
            titulo: `Marcar ${lead.nome} como perdido?`,
            descricao: 'O lead sai do quadro do funil. Continua na lista e nos relatórios.',
            confirmar: 'Marcar como perdido',
        });
        if (!ok) return;
        setSalvando(true);
        try {
            await dados().atualizarLead(lead.id, { situacao: 'perdido' });
            aviso({ title: 'Lead perdido', description: lead.nome, tone: 'warning' });
            onSalvo();
            onClose();
        } catch (e) {
            aviso({ title: 'Não foi possível marcar como perdido', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const nomeDoResponsavel = equipe.find((p) => p.id === form.responsavelId)?.nome ?? lead.responsavel;
    /* A etapa se troca dentro do pipeline do lead; mudar de pipeline é pelo
       quadro ou pela ação em lote, onde o nome do pipeline está à vista. */
    const opcoesDeEtapa = funil ? etapasAtivas(funil).map((e) => ({ value: e.id, label: e.titulo })) : [{ value: lead.etapaId, label: lead.etapa }];

    return (
        <>
            <Modal
                open onClose={onClose} width={620} title={lead.nome} description={`${lead.origem} · ${lead.interesse}`}
                dirty={sujo} onSave={salvar}
                footer={
                    <>
                        <Button variant="ghost" onClick={() => void marcarPerdido()} disabled={salvando || lead.situacao === 'perdido'}>Marcar perdido</Button>
                        <span style={{ marginLeft: 'auto' }} />
                        <Button variant="secondary" iconLeft={<Icon name="target" size={15} />} onClick={() => onVerNoFunil(lead)}>Ver no funil</Button>
                        {edicao
                            ? <Button onClick={() => void salvar()} loading={salvando} disabled={!sujo}>Salvar</Button>
                            : <Button onClick={() => void avancar()} loading={salvando}>Avançar etapa</Button>}
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar name={lead.nome} size="lg" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <Badge tone={TOM_DA_SITUACAO[lead.situacao]} dot>{lead.etapa}</Badge>
                                {atrasado && <Badge tone="danger" dot>Follow-up atrasado</Badge>}
                            </span>
                            <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>{lead.telefone}</span>
                        </div>
                        <span style={{ marginLeft: 'auto' }} />
                        {/* Perto do telefone de propósito: é a mesma decisão — falar
                            com esta pessoa —, e o canal se escolhe no clique. */}
                        <EnviarMensagem contatoId={lead.contatoId} nome={lead.nome} />
                        {!edicao && (
                            <IconButton variant="outline" icon={<Icon name="pencil" size={15} />} label="Editar lead" onClick={() => setEdicao(true)} />
                        )}
                    </div>

                    {edicao ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <FormField label="Nome" required htmlFor={idNome}><Input id={idNome} value={form.nome} onChange={(v) => alterar('nome', v)} invalid={!form.nome.trim() && !!erro} /></FormField>
                            <FormField label="Telefone" htmlFor={idTelefone}><Input id={idTelefone} value={form.telefone} onChange={(v) => alterar('telefone', v)} /></FormField>
                            <FormField label="Origem" htmlFor={idOrigem} hint="As que a clínica já usa aparecem como sugestão">
                                <Input id={idOrigem} list={idOrigens} value={form.origem} onChange={(v) => alterar('origem', v)} />
                            </FormField>
                            <datalist id={idOrigens}>{origens.map((o) => <option key={o} value={o} />)}</datalist>
                            <FormField label="Interesse" htmlFor={idInteresse}><Input id={idInteresse} value={form.interesse} onChange={(v) => alterar('interesse', v)} /></FormField>
                            <FormField label="Responsável" htmlFor={idResponsavel}>
                                <Select
                                    id={idResponsavel} value={form.responsavelId} onChange={(v) => alterar('responsavelId', v)} placeholder="Sem responsável"
                                    options={equipe.map((p) => ({ value: p.id, label: p.nome }))}
                                />
                            </FormField>
                            <FormField label="Valor estimado" hint="Em reais" htmlFor={idValor}>
                                <Input
                                    id={idValor} type="number" value={String(form.valorEstimadoCentavos / 100)}
                                    onChange={(v) => alterar('valorEstimadoCentavos', Math.max(0, Math.round(Number(v || 0) * 100)))}
                                />
                            </FormField>
                            <FormField label="Próximo contato" htmlFor={idProximo}>
                                <Input id={idProximo} type="date" value={form.proximoContatoEm} onChange={(v) => alterar('proximoContatoEm', v)} />
                            </FormField>
                            <FormField label="Etapa do funil" htmlFor={idEtapa} hint={funil && funis.length > 1 ? `Pipeline ${funil.nome}` : undefined}>
                                <Select id={idEtapa} value={form.etapaId} onChange={(v) => alterar('etapaId', v)} options={opcoesDeEtapa} />
                            </FormField>
                            {(campanhas.length > 0 || lead.campanhaDeAnuncioId) && (
                                <FormField
                                    label="Campanha de anúncio" htmlFor={idCampanha} style={{ gridColumn: '1 / -1' }}
                                    hint={lead.atribuicao === 'whatsapp-anuncio' && form.campanhaDeAnuncioId === original.campanhaDeAnuncioId
                                        ? 'Ligada sozinha: a conversa começou por este anúncio.'
                                        : 'É por ela que o painel de Anúncios conta o lead e o que ele rendeu.'}
                                >
                                    <Select
                                        id={idCampanha} value={form.campanhaDeAnuncioId} onChange={(v) => alterar('campanhaDeAnuncioId', v)}
                                        options={[
                                            { value: '', label: 'Nenhuma' },
                                            ...campanhas.map((c) => ({ value: c.id, label: c.nome })),
                                            ...(lead.campanhaDeAnuncioId && !campanhas.some((c) => c.id === lead.campanhaDeAnuncioId)
                                                ? [{ value: lead.campanhaDeAnuncioId, label: lead.campanhaDeAnuncio ?? 'Campanha atual' }]
                                                : []),
                                        ]}
                                    />
                                </FormField>
                            )}
                        </div>
                    ) : (
                        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '9px 16px', fontFamily: 'var(--font-ui)', fontSize: 13 }}>
                            {([
                                ['Origem', lead.origem],
                                ['Interesse', lead.interesse],
                                ['Responsável', nomeDoResponsavel],
                                ['Etapa do funil', etapaComFunil],
                                ['Criado em', dataSimples(lead.criadoEm)],
                                ['Próximo contato', texto],
                                ['Valor estimado', moeda(lead.valorEstimadoCentavos)],
                                ...(lead.campanhaDeAnuncioId
                                    ? [['Campanha de anúncio', `${lead.campanhaDeAnuncio ?? 'Campanha'}${lead.atribuicao ? ` (${FORMA_DA_ATRIBUICAO[lead.atribuicao]})` : ''}`] as const]
                                    : []),
                            ] as const).map(([rotulo, valor]) => (
                                <div key={rotulo} style={{ display: 'contents' }}>
                                    <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
                                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, color: String(valor).startsWith('Atrasado') ? 'var(--danger-strong)' : 'var(--text-primary)' }}>{valor}</dd>
                                </div>
                            ))}
                        </dl>
                    )}

                    <FormField label="Registrar contato" hint="Entra no histórico do contato" htmlFor={idNota}>
                        <Textarea id={idNota} value={nota} onChange={setNota} rows={2} placeholder="O que foi conversado, e o que ficou combinado…" />
                    </FormField>
                    {!edicao && (
                        <Button
                            size="sm" variant="secondary" disabled={!nota.trim() || salvando} style={{ alignSelf: 'flex-start' }}
                            onClick={() => {
                                void registrarNota()
                                    .then(() => aviso({ title: 'Contato registrado no histórico', description: lead.nome }))
                                    .catch((e: unknown) => setErro(e instanceof Error ? e.message : 'Não foi possível registrar.'));
                            }}
                        >
                            Registrar no histórico
                        </Button>
                    )}
                </div>
            </Modal>
            {confirmacao}
        </>
    );
}
